import { httpCoreService, httpService } from "@/services";
import type { ApiResponse } from "../types/auth.type";
import {
  AssignUserRolePayload,
  PrincipalInvestigator,
  StudyCordinator,
  UserRoleDefinition,
  UserListItem,
} from "@/types/user.type";
import { TherapeuticArea } from "@/types/therapeutic-area.type";
import { clearToken } from "@/utils/auth";
import { isApiError } from "@/utils/errorMessage";
import { toast } from "sonner";

export interface IUserRepository {
  getUsers(): Promise<UserListItem[]>;
  getUserRoles(): Promise<UserRoleDefinition[]>;
  assignRolesToUser(
    userId: string,
    payload: AssignUserRolePayload,
  ): Promise<void>;
  getPrincipalInvestigators(): Promise<PrincipalInvestigator[]>;
  getStudyCordinators(): Promise<StudyCordinator[]>;
  getTherapeuticAreas(): Promise<TherapeuticArea[]>;
}

export class UserRepository implements IUserRepository {
  async getUsers(): Promise<UserListItem[]> {
    try {
      const response: ApiResponse<UserListItem[]> =
        await httpService.get<UserListItem[]>("/users");

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) || "Failed to fetch users",
        );
      }

      if (!response.data) {
        throw new Error(
          "Request succeeded but no data was returned from the server.",
        );
      }

      return response.data;
    } catch (error: unknown) {
      // Handle 401 Unauthorized - clear token and logout user
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }

      // Re-throw the error with original message for other cases
      throw error;
    }
  }

  async getUserRoles(): Promise<UserRoleDefinition[]> {
    try {
      const response: ApiResponse<UserRoleDefinition[]> =
        await httpService.get<UserRoleDefinition[]>("/users/roles");

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) || "Failed to fetch user roles",
        );
      }

      return response.data || [];
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }
      throw error;
    }
  }

  async assignRolesToUser(
    userId: string,
    payload: AssignUserRolePayload,
  ): Promise<void> {
    try {
      const response: ApiResponse<unknown> = await httpService.post(
        `/users/${userId}/roles`,
        payload,
      );

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) || "Failed to assign user roles",
        );
      }
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }
      throw error;
    }
  }

  async getPrincipalInvestigators(): Promise<PrincipalInvestigator[]> {
    try {
      const response: ApiResponse<PrincipalInvestigator[]> =
        await httpService.get<PrincipalInvestigator[]>(
          "/users/principal-investigators",
        );

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) ||
            "Failed to fetch principal investigators",
        );
      }

      if (!response.data) {
        throw new Error(
          "Request succeeded but no data was returned from the server.",
        );
      }

      return response.data;
    } catch (error: unknown) {
      // Handle 401 Unauthorized - clear token and logout user
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }

      // Re-throw the error with original message for other cases
      throw error;
    }
  }

  async getStudyCordinators(): Promise<StudyCordinator[]> {
    try {
      const response: ApiResponse<StudyCordinator[]> = await httpService.get<
        StudyCordinator[]
      >("/users/study-cordinators");
      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) ||
            "Failed to fetch study coordinators",
        );
      }

      if (!response.data) {
        throw new Error(
          "Request succeeded but no data was returned from the server.",
        );
      }

      return response.data;
    } catch (error: unknown) {
      // Handle 401 Unauthorized - clear token and logout user
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }
      // Re-throw the error with original message for other cases
      throw error;
    }
  }

  async getTherapeuticAreas(): Promise<TherapeuticArea[]> {
    try {
      const response: ApiResponse<TherapeuticArea[]> =
        await httpCoreService.get<TherapeuticArea[]>("/therapeutic-areas");

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) ||
            "Failed to fetch therapeutic areas",
        );
      }

      if (!response.data) {
        throw new Error(
          "Request succeeded but no data was returned from the server.",
        );
      }

      return response.data;
    } catch (error: unknown) {
      // Handle 401 Unauthorized - clear token and logout user
      if (isApiError(error) && error.status === 401) {
        clearToken();
        toast.error("Your session has expired. Please login again.", {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
        throw new Error("Your session has expired. Please login again.");
      }

      // Re-throw the error with original message for other cases
      throw error;
    }
  }
}

// Singleton instance
export const userRepository = new UserRepository();
